import IconArrowLeft from "@/app/(components)/generics/Icons/IconArrowLeft";
import IconArrowRight from "@/app/(components)/generics/Icons/IconArrowRight";
import { PaginationData } from "@/app/types/common/PaginationData";
import { hasPages } from "./Pagination.logic";
import styles from "./Pagination.module.css";

type Props = {
  data: PaginationData;
  onPageChange: (page: number) => void;
  maxVisiblePages?: number;
};

export default function Pagination({ data, onPageChange, maxVisiblePages = 6 }: Props) {
  if (!hasPages(data)) {
    return null;
  }

  const getPageNumbers = (): (number | string)[] => {
    const totalPages = data.totalPages!;
    const current = data.page;

    const range = (start: number, end: number): number[] =>
      Array.from({ length: end - start + 1 }, (_, i) => start + i);

    // Show every page when collapsing wouldn't actually save room (the two
    // ellipses would hide at most 2 pages).
    if (totalPages <= maxVisiblePages + 2) {
      return range(1, totalPages);
    }

    // `maxVisiblePages` is the number of numeric buttons shown. Reserve first +
    // last; the rest forms the window centered on the current page.
    const siblingCount = Math.max(0, Math.floor((maxVisiblePages - 3) / 2));
    const edgeCount = maxVisiblePages - 1;

    const leftSibling = Math.max(current - siblingCount, 1);
    const rightSibling = Math.min(current + siblingCount, totalPages);

    const showLeftDots = leftSibling > 2;
    const showRightDots = rightSibling < totalPages - 1;

    if (!showLeftDots && showRightDots) {
      return [...range(1, edgeCount), "...", totalPages];
    }

    if (showLeftDots && !showRightDots) {
      return [1, "...", ...range(totalPages - edgeCount + 1, totalPages)];
    }

    return [1, "...", ...range(leftSibling, rightSibling), "...", totalPages];
  };

  return (
    <div className={styles.container}>
      <button
        onClick={() => onPageChange(data.page - 1)}
        disabled={!data.hasPrevious}
        className={styles.filterButton}
      >
        <IconArrowLeft />
      </button>

      {getPageNumbers().map((p, i) =>
        typeof p === "number" ? (
          <button
            key={i}
            onClick={() => onPageChange(p)}
            className={`${styles.filterButton} ${data.page === p ? styles.buttonActive : ""}`}
          >
            {p}
          </button>
        ) : (
          <span key={i} className={styles.ellipsis}>
            {p}
          </span>
        ),
      )}

      <button
        onClick={() => onPageChange(data.page + 1)}
        disabled={!data.hasNext}
        className={styles.filterButton}
      >
        <IconArrowRight />
      </button>
    </div>
  );
}
